import React, { useEffect, useState } from "react";
import { onAuthStateChanged, type User } from "firebase/auth";
import { auth } from "../../firebase";

export type BondReadyNavKey = "pipeline" | "tracker";

const tokens = {
  bg: "#0b111a",
  panel: "#121c29",
  panel2: "#172231",
  line: "#263548",
  line2: "#34445a",
  text: "#eef5ff",
  muted: "#9fb0c4",
  muted2: "#6f8095",
  green: "#62d288",
  green2: "#134733",
  amber: "#f1c46d",
  amber2: "#49391b",
  red: "#ff7b7b",
  red2: "#4b1f25",
  blue: "#7bb6ff",
  blue2: "#153352",
  purple: "#b992ff",
  shadow: "0 18px 60px rgba(0,0,0,.38)",
  radius: 16,
};

export const br: Record<string, React.CSSProperties> = {
  app: {
    minHeight: "100vh",
    display: "grid",
    gridTemplateColumns: "260px minmax(0, 1fr)",
    background: "radial-gradient(circle at top left, #18283a 0, #0b111a 34%, #071018 100%)",
    color: tokens.text,
    fontFamily: 'Arial, Helvetica, sans-serif',
    letterSpacing: "0.01em",
  },
  side: {
    background: "linear-gradient(180deg, #0d1622 0%, #0a111a 100%)",
    borderRight: `1px solid ${tokens.line}`,
    padding: "22px 18px",
    position: "sticky",
    top: 0,
    height: "100vh",
    boxSizing: "border-box",
    display: "flex",
    flexDirection: "column",
  },
  brand: { display: "flex", alignItems: "center", gap: 12, marginBottom: 20 },
  mark: {
    width: 38,
    height: 38,
    border: `1px solid ${tokens.line2}`,
    borderRadius: 12,
    background: "linear-gradient(135deg,#1b334d,#102338)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: 800,
    color: tokens.green,
  },
  brandTitle: {
    fontSize: 15,
    lineHeight: 1.15,
    margin: 0,
    fontWeight: 800,
    letterSpacing: "0.04em",
    textTransform: "uppercase" as const,
  },
  brandSub: { margin: "4px 0 0", color: tokens.muted, fontSize: 11 },
  nav: { display: "grid", gap: 8, marginTop: 18 },
  navLink: {
    width: "100%",
    textAlign: "left" as const,
    background: "transparent",
    color: tokens.muted,
    border: "1px solid transparent",
    borderRadius: 12,
    padding: "11px 12px",
    cursor: "pointer",
    fontWeight: 700,
    fontSize: 13,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    textDecoration: "none",
    boxSizing: "border-box" as const,
  },
  navActive: {
    background: "linear-gradient(135deg,#16283b,#101e2d)",
    color: "#fff",
    borderColor: "#35506d",
    boxShadow: `inset 3px 0 0 ${tokens.green}`,
  },
  sideFooter: {
    marginTop: "auto",
    display: "grid",
    gap: 10,
  },
  sideNote: {
    border: `1px solid ${tokens.line}`,
    borderRadius: 14,
    padding: 12,
    background: "#0d1621",
    color: tokens.muted,
    fontSize: 12,
    lineHeight: 1.42,
  },
  accountPanel: {
    border: `1px solid ${tokens.line}`,
    borderRadius: 14,
    padding: 12,
    background: "#0d1621",
    display: "grid",
    gap: 8,
  },
  accountName: {
    margin: 0,
    color: tokens.text,
    fontSize: 13,
    fontWeight: 800,
    lineHeight: 1.25,
    overflowWrap: "anywhere" as const,
  },
  accountEmail: {
    margin: 0,
    color: tokens.muted,
    fontSize: 12,
    lineHeight: 1.3,
    overflowWrap: "anywhere" as const,
  },
  signOutBtn: {
    width: "100%",
    textAlign: "left" as const,
    border: `1px solid ${tokens.line2}`,
    background: "#132033",
    color: tokens.text,
    borderRadius: 10,
    padding: "9px 11px",
    fontSize: 12,
    fontWeight: 800,
    cursor: "pointer",
  },
  main: { padding: "22px 26px 38px", minWidth: 0 },
  topbar: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 16,
    marginBottom: 18,
  },
  title: { fontSize: 22, margin: "0 0 6px", fontWeight: 850, letterSpacing: "0.01em" },
  subtitle: { margin: 0, color: tokens.muted, fontSize: 13, lineHeight: 1.4, maxWidth: 900 },
  banner: {
    border: "1px solid #36516d",
    background: "linear-gradient(135deg,rgba(26,45,65,.88),rgba(14,24,36,.88))",
    borderRadius: 16,
    padding: "14px 16px",
    marginBottom: 16,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 14,
    boxShadow: tokens.shadow,
  },
  card: {
    background: "linear-gradient(180deg,rgba(19,30,44,.96),rgba(14,22,33,.96))",
    border: `1px solid ${tokens.line}`,
    borderRadius: tokens.radius,
    boxShadow: tokens.shadow,
    overflow: "hidden",
  },
  cardHead: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 14,
    borderBottom: `1px solid ${tokens.line}`,
    padding: "14px 16px",
    background: "rgba(9,17,27,.42)",
  },
  cardBody: { padding: 16 },
  kpiGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(6, minmax(0, 1fr))",
    gap: 14,
    marginBottom: 16,
  },
  kpi: {
    padding: 14,
    borderRadius: 14,
    border: `1px solid ${tokens.line}`,
    background: "linear-gradient(180deg,#132033,#0f1927)",
  },
  kpiLabel: {
    color: tokens.muted,
    fontSize: 11,
    textTransform: "uppercase" as const,
    letterSpacing: "0.08em",
    fontWeight: 800,
  },
  kpiValue: { fontSize: 27, fontWeight: 900, margin: "8px 0 3px", color: "#fff" },
  kpiSub: { fontSize: 11, color: tokens.muted, lineHeight: 1.3 },
  gridTwo: { display: "grid", gridTemplateColumns: "1.12fr .88fr", gap: 14 },
  heroFlow: {
    display: "grid",
    gridTemplateColumns: "repeat(7, minmax(100px, 1fr))",
    gap: 8,
    alignItems: "stretch",
  },
  flowStep: {
    border: `1px solid ${tokens.line}`,
    background: "#101a27",
    borderRadius: 14,
    padding: 12,
    minHeight: 110,
    position: "relative" as const,
  },
  flowGood: { borderColor: "#397456", background: "#10291f" },
  flowBlocked: { borderColor: "#7a5f2d", background: "#2a2416" },
  callout: {
    border: "1px solid #385676",
    background: "#102033",
    borderRadius: 14,
    padding: 13,
    color: "#dfeaff",
    fontSize: 12,
    lineHeight: 1.45,
  },
  calloutWarn: {
    border: "1px solid #7a5f2d",
    background: "#2b2415",
    borderRadius: 14,
    padding: 13,
    color: "#ffe3a3",
    fontSize: 12,
    lineHeight: 1.45,
  },
  calloutGreen: {
    border: "1px solid #377452",
    background: "#102b21",
    borderRadius: 14,
    padding: 13,
    color: "#caffdc",
    fontSize: 12,
    lineHeight: 1.45,
  },
  calloutRed: {
    border: "1px solid #733943",
    background: "#2b171c",
    borderRadius: 14,
    padding: 13,
    color: "#ffcbd1",
    fontSize: 12,
    lineHeight: 1.45,
  },
  pill: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    border: `1px solid ${tokens.line2}`,
    borderRadius: 999,
    padding: "4px 8px",
    fontSize: 11,
    color: tokens.muted,
    background: "#0b1521",
    whiteSpace: "nowrap" as const,
    fontWeight: 700,
  },
  pillGreen: { color: "#b8ffd0", borderColor: "#2f7150", background: "#102b21" },
  pillAmber: { color: "#ffe3a3", borderColor: "#7a6130", background: "#2b2415" },
  pillRed: { color: "#ffc2c2", borderColor: "#7e3d45", background: "#2b161b" },
  pillBlue: { color: "#bedcff", borderColor: "#37618d", background: "#10243a" },
  pillPurple: { color: "#dac5ff", borderColor: "#654b93", background: "#201832" },
  pillLock: { color: "#d5dde8", borderColor: "#47586c", background: "#152131" },
  btn: {
    border: `1px solid ${tokens.line2}`,
    background: "#132033",
    color: "#fff",
    borderRadius: 11,
    padding: "10px 12px",
    fontSize: 12,
    fontWeight: 800,
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    textDecoration: "none",
  },
  btnPrimary: {
    background: "linear-gradient(135deg,#2b6c49,#174b34)",
    borderColor: "#3b8d63",
    color: "#eafff2",
  },
  label: {
    color: tokens.muted,
    fontSize: 11,
    fontWeight: 800,
    textTransform: "uppercase" as const,
    letterSpacing: "0.06em",
    marginBottom: 4,
  },
  value: { color: "#edf5ff", fontSize: 13, overflowWrap: "anywhere" as const },
  muted: { color: tokens.muted, fontSize: 12, lineHeight: 1.4 },
  field: {
    background: "#0b1420",
    border: `1px solid ${tokens.line2}`,
    borderRadius: 10,
    color: tokens.text,
    padding: "0.5rem",
    width: "100%",
    boxSizing: "border-box" as const,
    fontFamily: "inherit",
  },
  form: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(13rem, 1fr))",
    gap: "0.65rem",
    marginTop: "0.75rem",
  },
  ticket: {
    margin: "10px 0",
    border: "1px solid #2a3d52",
    borderRadius: 12,
    padding: 14,
    background: "#132033",
  },
  kanban: {
    display: "grid",
    gridTemplateColumns: "repeat(4, minmax(180px, 1fr))",
    gap: 12,
    overflow: "auto",
    paddingBottom: 8,
  },
  lane: {
    border: `1px solid ${tokens.line}`,
    borderRadius: 14,
    background: "#0f1825",
    minHeight: 180,
  },
  laneHead: {
    margin: 0,
    padding: 12,
    borderBottom: `1px solid ${tokens.line}`,
    fontSize: 12,
    color: "#d7e3f2",
  },
  empty: {
    padding: 16,
    color: tokens.muted2,
    fontSize: 12,
    textAlign: "center" as const,
    border: "1px dashed #2d3f55",
    borderRadius: 12,
    margin: 10,
  },
  sectionPad: { marginTop: 14 },
  gridAuto: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(12rem, 1fr))",
    gap: 12,
  },
};

export function Pill({
  children,
  tone = "default",
}: React.PropsWithChildren<{
  tone?: "default" | "green" | "amber" | "red" | "blue" | "purple" | "lock";
}>) {
  const toneStyle =
    tone === "green" ? br.pillGreen
      : tone === "amber" ? br.pillAmber
        : tone === "red" ? br.pillRed
          : tone === "blue" ? br.pillBlue
            : tone === "purple" ? br.pillPurple
              : tone === "lock" ? br.pillLock
                : {};
  return <span style={{ ...br.pill, ...toneStyle }}>{children}</span>;
}

export function Card({
  title,
  subtitle,
  badge,
  children,
  style,
  testId,
}: React.PropsWithChildren<{
  title?: string;
  subtitle?: string;
  badge?: React.ReactNode;
  style?: React.CSSProperties;
  testId?: string;
}>) {
  return (
    <section style={{ ...br.card, ...style }} data-testid={testId}>
      {(title || badge) && (
        <div style={br.cardHead}>
          <div>
            {title && <h3 style={{ fontSize: 14, margin: 0, fontWeight: 850 }}>{title}</h3>}
            {subtitle && <p style={{ fontSize: 12, color: "#9fb0c4", margin: "4px 0 0", lineHeight: 1.4 }}>{subtitle}</p>}
          </div>
          {badge}
        </div>
      )}
      <div style={br.cardBody}>{children}</div>
    </section>
  );
}

type NavItem = {
  key: BondReadyNavKey;
  label: string;
  index: string;
  href: string;
};

const NAV_ITEMS: NavItem[] = [
  { key: "pipeline", label: "Portfolio", index: "01", href: "/bondready" },
  { key: "tracker", label: "Partner Activity", index: "02", href: "/bondready?section=tracker" },
];

export default function BondReadyShell({
  activeNav,
  title,
  subtitle,
  children,
  topActions,
}: React.PropsWithChildren<{
  activeNav: BondReadyNavKey;
  title: string;
  subtitle: string;
  opportunityHref?: string | null;
  topActions?: React.ReactNode;
}>) {
  const [authUser, setAuthUser] = useState<User | null>(() => auth.currentUser);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setAuthUser(user);
    });
    return unsubscribe;
  }, []);

  const displayName = authUser?.displayName?.trim() || "";
  const email = authUser?.email?.trim() || "";

  const handleSignOut = () => {
    auth.signOut();
  };

  return (
    <div className="bondready-app" style={br.app} data-testid="bondready-shell">
      <aside style={br.side} data-testid="bondready-nav" aria-label="BondReady navigation">
        <div style={br.brand}>
          <div style={br.mark}>CP</div>
          <div>
            <h1 style={br.brandTitle}>
              ConstructPay
              <br />
              BondReady
            </h1>
            <p style={br.brandSub}>Portfolio · Opportunity · Request</p>
          </div>
        </div>
        <nav style={br.nav} aria-label="BondReady sections">
          {NAV_ITEMS.map((item) => {
            const active = item.key === activeNav;
            return (
              <a
                key={item.key}
                href={item.href}
                aria-current={active ? "page" : undefined}
                style={{
                  ...br.navLink,
                  ...(active ? br.navActive : {}),
                }}
                data-testid={`bondready-nav-${item.key}`}
              >
                <span>{item.label}</span>
                <span>{item.index}</span>
              </a>
            );
          })}
        </nav>
        <div style={br.sideFooter}>
          <div style={br.sideNote}>
            Start with the portfolio work queue, open an opportunity lifecycle, then inspect specific bond or funding requests.
          </div>
          <div
            style={br.accountPanel}
            data-testid="bondready-account-control"
            aria-label="Account"
          >
            {displayName ? (
              <p style={br.accountName} data-testid="bondready-account-name">
                {displayName}
              </p>
            ) : null}
            {email ? (
              <p style={br.accountEmail} data-testid="bondready-account-email">
                {email}
              </p>
            ) : null}
            {!displayName && !email ? (
              <p style={br.accountEmail} data-testid="bondready-account-session">
                Signed in
              </p>
            ) : null}
            <button
              type="button"
              style={br.signOutBtn}
              onClick={handleSignOut}
              data-testid="bondready-sign-out"
            >
              Sign out
            </button>
          </div>
        </div>
      </aside>

      <main style={br.main}>
        <div style={br.topbar}>
          <div>
            <h2 style={br.title}>{title}</h2>
            <p style={br.subtitle}>{subtitle}</p>
          </div>
          {topActions && <div style={{ display: "flex", gap: 10, flexWrap: "wrap", justifyContent: "flex-end" }}>{topActions}</div>}
        </div>
        {children}
      </main>

      <style>{`
        @media (max-width: 1180px) {
          .bondready-app { grid-template-columns: 1fr !important; }
          .bondready-app aside { position: relative !important; height: auto !important; }
          .bondready-kpi-grid { grid-template-columns: repeat(3, minmax(0, 1fr)) !important; }
          .bondready-hero-flow { grid-template-columns: repeat(2, minmax(0, 1fr)) !important; }
          .bondready-grid-two { grid-template-columns: 1fr !important; }
        }
        @media (max-width: 720px) {
          .bondready-kpi-grid { grid-template-columns: 1fr !important; }
          .bondready-kanban { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </div>
  );
}

export function gate2Display(state: any): { label: string; tone: "lock" | "amber" | "green" | "red" } {
  if (state === "AUTHORIZED") return { label: "External sharing authorized", tone: "green" };
  if (state === "DENIED" || state === "REVOKED") return { label: "External sharing declined", tone: "red" };
  if (state === "PENDING" || state === "INITIAL" || !state) {
    return { label: "External sharing not yet authorized", tone: state === "PENDING" ? "amber" : "lock" };
  }
  return { label: "External sharing locked", tone: "lock" };
}

export function disclosureReasonLabel(reason: any): string {
  if (reason === "GATE_2_AUTHORIZATION_REQUIRED") {
    return "External sharing requires contractor authorization before company-specific disclosure";
  }
  if (reason === "SOURCE_DATA_CONFLICT") return "Source data conflict blocks disclosure";
  if (reason === "GATE_2_AUTHORIZED") return "Contractor authorization recorded for approved disclosure scope";
  if (!reason) return "Unknown / not supplied";
  return String(reason);
}
